import { useEffect } from "react";
import { Button, Result, Space, Typography } from "antd";
import { useNavigate } from "react-router-dom";

import { Markdown } from "@components/Text";
import { getConfig } from "@components/Config";
import { usePublicOrganizationContext } from "@components/AppContext";

import { signOut, getIdentityEmail } from "./authenticate/index.js";

const consoleOrganizationId = getConfig("consoleOrganizationId");
const PLATFORM_EMAIL = getConfig("PLATFORM_EMAIL");

const { Text } = Typography;

const LABEL_TITLE = "Authorization Failed";
// The token carries the address. A signed out session carries no token,
// therefore the copy names the address only when the token holds it.
const LABEL_KNOWN_EMAIL = "The email address **[IDENTITY_EMAIL]**";
const LABEL_UNKNOWN_EMAIL = "Your email address";
const LABEL_BACKSTAGE = `[EMAIL_ADDRESS] was not recognized as an admin member
of the [ORGANIZATION_NAME].

If you are trying to log in to the investor portal, please [CLICK_HERE]. If you
need assistance with your account setup, please reach out to us via email at
[ORGANIZATION_EMAIL].`;
const LABEL_CLICK_HERE = "click here";
const LABEL_INVESTOR_PORTAL = `[EMAIL_ADDRESS] was not recognized.

If you need assistance with your account setup, please reach out to us via
email at [ORGANIZATION_EMAIL].
`;

const LABEL_SIGNOUT = "Sign Out";
const LABEL_LOADING = "Loading...";
const LABEL_NOT_READY = "Organization configuration is not ready yet.";
const LABEL_REDIRECTING = "Redirecting...";

const BACKSTAGE_PATH = "/backstage";

// The context holds an empty object until ReadPublicOrganization answers,
// therefore each member is optional.
type PublicOrganization = {
  id?: string;
  name?: string;
  email?: string;
  isReady?: boolean;
  logoUrl?: string;
};

type PublicOrganizationContext = {
  publicOrganization: PublicOrganization;
};

type Props = {
  message?: string;
  isBackstage: boolean;
};

const AuthorizationError = ({
  message = undefined,
  isBackstage,
}: Props) => {
  const navigate = useNavigate();
  const { publicOrganization } =
    usePublicOrganizationContext() as PublicOrganizationContext;

  const isConsoleOrganization = publicOrganization.id === consoleOrganizationId;

  const shouldRedirectToConsoleBackstage =
    isConsoleOrganization &&
    !isBackstage;

  useEffect(() => {
    if (!shouldRedirectToConsoleBackstage) {
      return;
    }

    window.location.href = BACKSTAGE_PATH;
  }, [ shouldRedirectToConsoleBackstage ]);

  if (shouldRedirectToConsoleBackstage) {
    return (
      <Text type="secondary">
        {LABEL_REDIRECTING}
      </Text>
    );
  }

  const rootPath = isBackstage
    ? BACKSTAGE_PATH
    : "/";

  const onClick = async () => {
    await signOut();
    navigate(rootPath);
  };

  const {
    name: organizationName,
    email: organizationEmail,
    isReady: isOrganizationReady,
  } = publicOrganization;

  const isLoaded = !!organizationName;

  const style = {
    margin: "0 auto",
    maxWidth: 520,
  };

  const hasCustomMessage = !!message;

  let formattedNotice: string;

  if (hasCustomMessage) {
    formattedNotice = message;
  } else if (!isLoaded) {
    formattedNotice = LABEL_LOADING;
  } else if (isOrganizationReady) {
    const identityEmail = getIdentityEmail(isBackstage);

    const hasIdentityEmail = !!identityEmail;

    const emailAddress = hasIdentityEmail
      ? LABEL_KNOWN_EMAIL.replace("[IDENTITY_EMAIL]", identityEmail)
      : LABEL_UNKNOWN_EMAIL;

    // The organization holds no address in some cases, and the platform
    // address is the fallback that keeps the link usable.
    const contactEmail = organizationEmail || PLATFORM_EMAIL;

    const mailTo = `[${contactEmail}](mailto:${contactEmail})`;

    const notice = isBackstage
      ? LABEL_BACKSTAGE
      : LABEL_INVESTOR_PORTAL;

    formattedNotice = notice
      .replace("[ORGANIZATION_EMAIL]", mailTo)
      .replace("[ORGANIZATION_NAME]", organizationName)
      .replace("[CLICK_HERE]", `[${LABEL_CLICK_HERE}](/)`)
      .replace("[EMAIL_ADDRESS]", emailAddress);

  } else {
    formattedNotice = LABEL_NOT_READY;

  }

  return (
    <Result
      icon={false}
      title={LABEL_TITLE}
      style={style}
      status="error"
      subTitle={[
        <Space
          key="extra"
          direction="vertical"
        >
          <Text type="secondary">
            <Markdown text={formattedNotice} />
          </Text>

          <Button onClick={onClick}>
            {LABEL_SIGNOUT}
          </Button>
        </Space>,
      ]}
    />
  );
};

export default AuthorizationError;
